// src/components/admin/ModuleManager.jsx - Updated to count Premium AI Tools
import React, { useState, useEffect } from 'react';
import { useModules } from '../../contexts/ModuleContext';
import ModuleCard from './ModuleCard';
import SupportTicketModal from './SupportTicketModal';
import SupportTicketHistory from './SupportTicketHistory';
import '../../styles/ModuleManagerandCard.css';
import '../../styles/responsive-utils.css';
import '../../styles/ModuleManagerandCard-responsive.css';
import '../../styles/tickets.css';

function ModuleManager({ salonId }) {
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [showTicketHistory, setShowTicketHistory] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);

  // Block Ctrl/Cmd+S (save) and Ctrl/Cmd+P (print) while the manual is open
  useEffect(() => {
    if (!showManualModal) return;
    const blockSaveAndPrint = (e) => {
      if ((e.ctrlKey || e.metaKey) && ['s', 'p'].includes(e.key.toLowerCase())) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', blockSaveAndPrint, { capture: true });
    return () => window.removeEventListener('keydown', blockSaveAndPrint, { capture: true });
  }, [showManualModal]);

  const {
    getEnabledModules,
    getAllModules,
    activeModule,
    switchToModule,
    isModuleEnabled
  } = useModules();

  const enabledModules = getEnabledModules();
  const allModules = getAllModules();

  // Filter out the 'core' module from display (it's accessed via 'home' card)
  const displayModules = allModules.filter(module => module.id !== 'core');

  // Count enabled modules excluding 'home' (which is not a real module, just a landing page)
  const activeModulesCount = enabledModules.filter(module => module.id !== 'home').length;

  const handleModuleClick = (moduleId, moduleEnabled) => {
    console.log('ModuleManager: handleModuleClick called with:', moduleId, 'enabled:', moduleEnabled);
    
    // Only proceed if module is enabled
    if (!moduleEnabled) {
      console.log('Module is disabled, not navigating');
      return;
    }
    
    // Use URL navigation for enabled modules
    if (moduleId === null) {
      window.location.href = `/BespokeBeauty/${salonId}/admin/allmodules`;
    } else if (moduleId === 'home') {
      // Home card routes to core module page (where all tabs are accessible)
      window.location.href = `/BespokeBeauty/${salonId}/admin/core`;
    } else if (moduleId === 'core') {
      window.location.href = `/BespokeBeauty/${salonId}/admin/core`;
    } else if (moduleId === 'bookings') {
      window.location.href = `/BespokeBeauty/${salonId}/admin/book`;
    } else if (moduleId === 'promotions') {
      window.location.href = `/BespokeBeauty/${salonId}/admin/promotion`;
    } else if (moduleId === 'communications') {
      window.location.href = `/BespokeBeauty/${salonId}/admin/campaigns`;
    } else if (moduleId === 'reports') {
      window.location.href = `/BespokeBeauty/${salonId}/admin/analytics`;
    } else if (moduleId === 'bespokeai') {
      window.location.href = `/BespokeBeauty/${salonId}/admin/ai`;
    }
  };

  /**
   * Calculate the number of Premium Tools (AI Tools) that are enabled
   * Premium Tools are:
   * - Reports AI (reports-ai-agent, under Reports Management)
   * - Communications AI (communications-ai, under Bespoke AI)
   * - Loyalty AI (loyalty-ai-agent, under Loyalty Management)
   * - Bookings AI (bookings-ai-agent, under Bookings Management)
   */
  const getPremiumToolsCount = () => {
    // Find the BespokeAI module
    const bespokeAIModule = allModules.find(module => module.id === 'bespokeai');
    
    // If BespokeAI module doesn't exist or is not enabled, return 0
    if (!bespokeAIModule || !bespokeAIModule.enabled) {
      return 0;
    }
    
    // Each AI agent lives under its own management module (not under bespokeai).
    // Count by checking for the agent tab under the correct module.
    let enabledPremiumTools = allModules.find(m => m.id === 'bookings')?.tabs?.includes('bookings-ai-agent') ? 1 : 0;
    if (allModules.find(m => m.id === 'reports')?.tabs?.includes('reports-ai-agent')) enabledPremiumTools++;
    if (allModules.find(m => m.id === 'promotions')?.tabs?.includes('loyalty-ai-agent')) enabledPremiumTools++;
    if (allModules.find(m => m.id === 'communications')?.tabs?.includes('communications-ai-agent')) enabledPremiumTools++;
    
    return enabledPremiumTools;
  };

  const getModuleGridClass = () => {
    const moduleCount = displayModules.length;
    if (moduleCount <= 2) return 'modules-grid-2';
    if (moduleCount <= 4) return 'modules-grid-4';
    return 'modules-grid-6';
  };

  // Debug logging
  console.log('ModuleManager render - enabledModules:', enabledModules);
  console.log('ModuleManager render - allModules:', allModules);
  console.log('ModuleManager render - activeModule:', activeModule);
  console.log('ModuleManager render - Premium Tools Count:', getPremiumToolsCount());

  // Show ticket history view if active
  if (showTicketHistory) {
    return (
      <SupportTicketHistory
        salonId={salonId}
        onBack={() => setShowTicketHistory(false)}
      />
    );
  }

  return (
    <div className="module-manager">
      <div className="module-manager-header">
        <h2>
          <span style={{ fontFamily: "'Fraunces', serif", color: 'var(--primary-coral)' }}>BespokeBeauty</span> Management Modules
        </h2>
        <p>Select a module to access its management tools and features</p>
      </div>

      <div className="module-overview-stats">
        <div className="overview-stat">
          <div className="stat-number">{activeModulesCount}</div>
          <div className="stat-label">Active Modules</div>
        </div>
        <div className="overview-stat">
          <div className="stat-number">
            {enabledModules.filter(module => module.id !== 'home').reduce((total, module) => total + module.tabs.length, 0)}
          </div>
          <div className="stat-label">Available Tools</div>
        </div>
        <div className="overview-stat">
          <div className="stat-number">
            {getPremiumToolsCount()}
          </div>
          <div className="stat-label">Premium Tools</div>
        </div>
      </div>

      <div className={`modules-grid ${getModuleGridClass()}`}>
        {displayModules.map((module) => (
          <ModuleCard
            key={module.id}
            module={module}
            isActive={activeModule === module.id}
            onClick={handleModuleClick}
            salonId={salonId}
          />
        ))}
      </div>

      <div className="module-help-section">
        <h3>Need Help?</h3>
        <div className="help-cards">
          <div className="help-card">
            <h4>Getting Started</h4>
            <p>New to the system? Click{' '}
              <button
                onClick={() => setShowManualModal(true)}
                className="manual-inline-link"
              >here</button>
              {' '}for the online manual to get assistance navigating the application.
            </p>
          </div>
          <div className="help-card">
            <h4>Technical Support</h4>
            <p>
              <a href="#" onClick={(e) => { e.preventDefault(); setShowTicketModal(true); }} style={{ color: 'var(--primary-coral)', textDecoration: 'underline' }}>Submit a support ticket</a> for bug reports, troubleshooting, and technical assistance.
            </p>
            <p style={{ marginTop: '0.5rem' }}>
              <a href="#" onClick={(e) => { e.preventDefault(); setShowTicketHistory(true); }} style={{ color: 'var(--primary-coral)', textDecoration: 'underline' }}>View my tickets</a>
            </p>
          </div>
          <div className="help-card">
            <h4>Other Services</h4>
            <p>Click <a href="https://www.bespokecrm.cloud/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-coral)', textDecoration: 'underline' }}>here</a> to find out more about other services which Bespoke CRM can assist in to add value to your business.</p>
          </div>
          <div className="help-card">
            <h4>Upgrade Features</h4>
            <p>Contact <a href="mailto:sales@bespokecrm.cloud">sales</a> to unlock premium modules and enhance your salon's capabilities.</p>
          </div>
        </div>
      </div>

      {showTicketModal && (
        <SupportTicketModal
          salonId={salonId}
          onClose={() => setShowTicketModal(false)}
        />
      )}

      {/* ── Training Manual viewer — view-only, no download ── */}
      {showManualModal && (
        <div
          className="manual-modal-overlay"
          onClick={() => setShowManualModal(false)}
          onContextMenu={e => e.preventDefault()}
        >
          <div
            className="manual-modal-container"
            onClick={e => e.stopPropagation()}
            onContextMenu={e => e.preventDefault()}
          >
            <div className="manual-modal-header">
              <span className="manual-modal-title">
                Admin Portal Training Manual v1.3
              </span>
              <span className="manual-modal-view-only-badge" aria-label="View only">
                👁 View only
              </span>
              <button
                className="manual-modal-close"
                onClick={() => setShowManualModal(false)}
                aria-label="Close manual"
              >
                ✕
              </button>
            </div>
            {/* Frame wrapper: overflow:hidden lets the Firefox-specific CSS
                shift the iframe up so its built-in PDF toolbar is clipped.
                Chrome/Edge already suppress the toolbar via #toolbar=0. */}
            <div
              className="manual-modal-frame-wrapper"
              onContextMenu={e => e.preventDefault()}
            >
              <iframe
                src="/BespokeBeauty_Admin_Portal_Training_Manual_v1.3.pdf#toolbar=0&navpanes=0&scrollbar=1"
                title="Admin Portal Training Manual v1.3"
                className="manual-modal-frame"
                onContextMenu={e => e.preventDefault()}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ModuleManager;